import type { EmployeeRow } from '../employeeTypes';
import type { DataGroupKey } from './dataGroups';
import type { SectionKey } from './employeeDetailTokens';
import type { SectionDef } from '../../../components/ui/sectionCard/types';
import type { LineId, PermissionAction } from '../../permissions/permissionTypes';
export type { FieldType, FieldDef, SectionDef } from '../../../components/ui/sectionCard/types';

export const TABS = [
  'identite',
  'contrat',
  'competence',
  'carriere',
  'evaluation',
  'conges',
  'paiement',
] as const;

export type TabKey = (typeof TABS)[number];

/**
 * RBAC catalogue line gating each tab's display (HOMCO-640 follow-up). A tab is
 * shown only when the connected profile is granted `display` on its line; the
 * parent `employee_record` line gates access to the record page as a whole (route
 * guard in `router.tsx`). Mirrors the backend catalogue
 * (`permission-catalogue.ts`).
 */
export const TAB_PERMISSION: Record<TabKey, LineId> = {
  identite: 'employee_record.identity',
  contrat: 'employee_record.contract',
  competence: 'employee_record.skills',
  carriere: 'employee_record.career_training',
  evaluation: 'employee_record.evaluation',
  conges: 'employee_record.leaves_absences',
  paiement: 'employee_record.payment',
};

/**
 * TABS indexes the connected profile may display, in TABS order. The index is
 * kept (rather than the key) because the page renders tab content by numeric
 * index. Empty when no tab is granted.
 */
export function visibleTabIndexes(
  can: (line: LineId, action: PermissionAction) => boolean,
): number[] {
  return TABS.reduce<number[]>((acc, key, index) => {
    if (can(TAB_PERMISSION[key], 'display')) acc.push(index);
    return acc;
  }, []);
}

/**
 * Data group for identity documents (not a SectionDef — rendered separately).
 */
export const IDENTITY_DOCUMENTS_GROUP: DataGroupKey = 'contract';

/**
 * Data group for emergency contacts (not a SectionDef — rendered separately via EmergencyContactsCard).
 */
export const EMERGENCY_CONTACTS_GROUP: DataGroupKey = 'classified_2';

/**
 * Data group for dependents (not a SectionDef — rendered separately).
 */
export const DEPENDENTS_GROUP: DataGroupKey = 'classified_2';

export function computeAge(birthdate: string | undefined | null): string | null {
  if (!birthdate) return null;
  const birth = new Date(birthdate);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    age--;
  }
  return age >= 0 ? String(age) : null;
}

export const EMPLOYEE_SECTIONS: SectionDef<EmployeeRow, SectionKey>[] = [
  // ── Column 1 ────────────────────────────────────────────────────────────────
  {
    id: 'personal',
    group: 'gdpr',
    column: 1,
    // Always open and non-foldable: the primary block stays expanded with no
    // chevron, while every other block starts folded.
    defaultExpanded: true,
    collapsible: false,
    titleKey: 'employeeDetail.sections.personal',
    fields: [
      // Row 1: civility · gender
      {
        key: 'civility',
        labelKey: 'employeeDetail.fields.civility',
        type: 'labeled-code',
        translatePrefix: 'employeeTables.filterOptions.civility',
      },
      {
        key: 'gender',
        labelKey: 'employeeDetail.fields.gender',
        type: 'labeled-code',
        translatePrefix: 'employeeTables.filterOptions.gender',
      },
      // Row 2: first name · last name
      { key: 'first_name', labelKey: 'employeeTables.columns.firstName', type: 'text' },
      { key: 'last_name', labelKey: 'employeeTables.columns.lastName', type: 'text' },
      // Row 3: file number · employee card number
      { key: 'file_number', labelKey: 'employeeTables.columns.fileNumber', type: 'text' },
      {
        key: 'employee_card_number',
        labelKey: 'employeeDetail.fields.employeeCardNumber',
        type: 'text',
      },
      // Row 4: birthdate · age
      { key: 'birthdate', labelKey: 'employeeTables.columns.birthdate', type: 'date' },
      {
        labelKey: 'employeeDetail.fields.age',
        type: 'text',
        compute: e => computeAge(e.birthdate),
      },
      // Row 5: birth place · nationality
      { key: 'birth_place', labelKey: 'employeeDetail.fields.birthPlace', type: 'text' },
      {
        key: 'nationality',
        labelKey: 'employeeDetail.fields.nationality',
        type: 'labeled-code',
        translatePrefix: 'employeeTables.filterOptions.country',
      },
      // Row 6: marital status · social security number
      {
        key: 'marital_status',
        labelKey: 'employeeDetail.fields.maritalStatus',
        type: 'labeled-code',
        translatePrefix: 'employeeTables.filterOptions.maritalStatus',
      },
      {
        key: 'social_security_number',
        labelKey: 'employeeDetail.fields.socialSecurityNumber',
        type: 'text',
      },
      // Row 7: entry date · staff representative
      { key: 'entry_date', labelKey: 'employeeTables.columns.entryDate', type: 'date' },
      {
        key: 'staff_representative',
        labelKey: 'employeeDetail.fields.staffRepresentative',
        type: 'labeled-code',
        translatePrefix: 'employeeTables.filterOptions.yesNo',
      },
      // Row 8: date updated · updated by
      { key: 'update_date', labelKey: 'employeeDetail.fields.dateUpdated', type: 'date' },
      { key: 'updated_by', labelKey: 'employeeDetail.fields.updatedBy', type: 'text' },
    ],
  },
  {
    id: 'death',
    group: 'gdpr',
    // Column 2, rendered right after the Contact/Emergency pair
    // (see EmployeeDetailPage `afterContactCards`).
    column: 2,
    // Always rendered (as a collapsed block); previously gated behind `deceased`.
    // The card carries "bénéficiaire en cas de décès" (death_beneficiary), which is
    // relevant regardless of whether the employee is deceased, so gating on `deceased`
    // wrongly hid the beneficiary field for living employees.
    titleKey: 'employeeDetail.sections.death',
    fields: [
      { key: 'deceased', labelKey: 'employeeDetail.fields.deceased', type: 'boolean' },
      { key: 'cause_of_death', labelKey: 'employeeDetail.fields.causeOfDeath', type: 'text' },
      {
        key: 'other_cause_of_death',
        labelKey: 'employeeDetail.fields.otherCauseOfDeath',
        type: 'text',
      },
      { key: 'date_of_death', labelKey: 'employeeDetail.fields.dateOfDeath', type: 'date' },
      {
        key: 'death_beneficiary',
        labelKey: 'employeeDetail.fields.beneficiaryDeath',
        type: 'text',
      },
    ],
  },
  {
    id: 'pins',
    group: 'gdpr',
    // Column 2, rendered right after En cas de décès
    // (see EmployeeDetailPage `afterContactCards`).
    column: 2,
    // Always rendered; only the populated PIN fields are listed (per-terrain).
    hideEmptyFields: true,
    titleKey: 'employeeDetail.sections.pins',
    fields: [
      {
        key: 'pin_1',
        labelKey: 'employeeDetail.fields.pin1',
        type: 'text',
        dynamicLabelKey: 'pin_1_label',
      },
      {
        key: 'pin_2',
        labelKey: 'employeeDetail.fields.pin2',
        type: 'text',
        dynamicLabelKey: 'pin_2_label',
      },
      {
        key: 'pin_3',
        labelKey: 'employeeDetail.fields.pin3',
        type: 'text',
        dynamicLabelKey: 'pin_3_label',
      },
      {
        key: 'pin_4',
        labelKey: 'employeeDetail.fields.pin4',
        type: 'text',
        dynamicLabelKey: 'pin_4_label',
      },
      {
        key: 'pin_5',
        labelKey: 'employeeDetail.fields.pin5',
        type: 'text',
        dynamicLabelKey: 'pin_5_label',
      },
      {
        key: 'pin_6',
        labelKey: 'employeeDetail.fields.pin6',
        type: 'text',
        dynamicLabelKey: 'pin_6_label',
      },
      {
        key: 'pin_7',
        labelKey: 'employeeDetail.fields.pin7',
        type: 'text',
        dynamicLabelKey: 'pin_7_label',
      },
      {
        key: 'pin_8',
        labelKey: 'employeeDetail.fields.pin8',
        type: 'date',
        dynamicLabelKey: 'pin_8_label',
      },
      {
        key: 'pin_9',
        labelKey: 'employeeDetail.fields.pin9',
        type: 'date',
        dynamicLabelKey: 'pin_9_label',
      },
      {
        key: 'pin_10',
        labelKey: 'employeeDetail.fields.pin10',
        type: 'date',
        dynamicLabelKey: 'pin_10_label',
      },
      {
        key: 'pin_11',
        labelKey: 'employeeDetail.fields.pin11',
        type: 'text',
        dynamicLabelKey: 'pin_11_label',
      },
      {
        key: 'pin_12',
        labelKey: 'employeeDetail.fields.pin12',
        type: 'text',
        dynamicLabelKey: 'pin_12_label',
      },
      {
        key: 'pin_13',
        labelKey: 'employeeDetail.fields.pin13',
        type: 'text',
        dynamicLabelKey: 'pin_13_label',
      },
    ],
  },

  // ── Column 2 ────────────────────────────────────────────────────────────────
  {
    id: 'address',
    group: 'gdpr',
    column: 2,
    titleKey: 'employeeDetail.sections.address',
    fields: [
      // `city` permanently omitted — no DB column; address is monobloc
      { key: 'address', labelKey: 'employeeDetail.fields.address', type: 'text', fullWidth: true },
      {
        key: 'country',
        labelKey: 'employeeDetail.fields.country',
        type: 'labeled-code',
        translatePrefix: 'employeeTables.filterOptions.country',
      },
    ],
  },
  {
    id: 'contact',
    group: 'classified_2',
    column: 2,
    stack: true,
    titleKey: 'employeeDetail.sections.contact',
    fields: [
      {
        key: 'home_phone',
        labelKey: 'employeeDetail.fields.homePhone',
        type: 'phone',
        fullWidth: true,
      },
      {
        key: 'office_phone',
        labelKey: 'employeeDetail.fields.officePhone',
        type: 'phone',
        fullWidth: true,
      },
      { key: 'phone', labelKey: 'employeeDetail.fields.cellPhone', type: 'phone', fullWidth: true },
      { key: 'fax', labelKey: 'employeeDetail.fields.fax', type: 'text', fullWidth: true },
      { key: 'email', labelKey: 'employeeDetail.fields.email', type: 'email', fullWidth: true },
      {
        key: 'email_pro',
        labelKey: 'employeeDetail.fields.emailPro',
        type: 'email',
        fullWidth: true,
      },
    ],
  },
  {
    id: 'comment',
    group: 'gdpr',
    column: 2,
    stack: true,
    titleKey: 'employeeDetail.sections.comment',
    fields: [
      { key: 'comment', labelKey: 'employeeDetail.fields.comment', type: 'text', fullWidth: true },
    ],
  },
];
